<template>
  <div v-if="hasAccess">
    <b-card>
      <h1>{{ company.name }}</h1>
      <br>

      <b-row>
        <b-col cols="4">
          <h4 class="mb-0 text-secondary font-weight-bold">
            FCA number
          </h4>
          <h3 class="mb-0">
            {{ company.fcanumber }}
          </h3>
        </b-col>

        <b-col cols="4">
          <h4 class="mb-0 text-secondary font-weight-bold">
            Subscription
          </h4>
          <h3 class="mb-0">
            {{ companySubscriptionLabel }}
          </h3>
        </b-col>
      </b-row>
    </b-card>
    <!--
<b-col md="4" sm="6" v-if="company.calltoaction" class="mb-2">
  <div class="text-muted">Call to Action</div>
  <b-button variant="success" size="sm">Call to action</b-button>
</b-col>
-->
    <b-card>
      <b-card-title>Pending email suffix requests</b-card-title>
      <b-alert
        v-if="pendingSuffixError"
        variant="danger"
        show
      >
        {{ pendingSuffixError }}
        <b-button
          variant="outline-danger"
          size="sm"
          class="ml-1"
          :disabled="loadingPendingSuffixes"
          @click.prevent="getPendingSuffixRequests"
        >
          Retry
        </b-button>
      </b-alert>
      <b-table
        v-else
        :items="pendingSuffixRequests"
        :fields="pendingSuffixRequestsHeader"
        :busy="loadingPendingSuffixes"
        primary-key="pendingid"
        responsive
        show-empty
        empty-text="There are currently no pending suffix requests"
      >
        <template #table-busy>
          <div class="text-center my-2">
            Loading pending suffix requests...
          </div>
        </template>
        <template #cell(actions)="data">
          <b-button
            variant="primary"
            :to="{ name: 'suffixapproval', params: { suffixPendingId: String(data.item.pendingid) } }"
          >
            Review
          </b-button>
        </template>
      </b-table>
    </b-card>

    <b-card>
      <b-card-title class="d-flex justify-content-between align-items-center">
        <span>New users waiting for approval</span>
        <div class="d-flex align-items-center">
          <b-button
            variant="success"
            size="sm"
            :disabled="!usersWaitingForApproval.length || isProcessingAnyApproval()"
            @click.prevent="confirmProcessAllUsersWaitingForApproval('approve')"
          >
            {{ isProcessingAllApprovals('approve') ? 'Approving all' : 'Approve all' }}
          </b-button>
          <b-button
            variant="danger"
            size="sm"
            class="ml-1"
            :disabled="!usersWaitingForApproval.length || isProcessingAnyApproval()"
            @click.prevent="confirmProcessAllUsersWaitingForApproval('reject')"
          >
            {{ isProcessingAllApprovals('reject') ? 'Rejecting all' : 'Reject all' }}
          </b-button>
        </div>
      </b-card-title>
      <b-alert
        v-if="approvalActionError"
        variant="danger"
        show
      >
        {{ approvalActionError }}
      </b-alert>
      <b-table
        :items="usersWaitingForApproval"
        :fields="usersWaitingForApprovalHeader"
        :tbody-tr-class="approvalRowClass"
        hover
        show-empty
        empty-text="There are currently no users waiting for approval"
        @row-clicked="goToUserApproval"
      >
        <template #cell(actions)="data">
          <div class="d-flex align-items-center">
            <b-button
              variant="success"
              class="ml-1"
              :disabled="isProcessingAnyApproval()"
              @click.stop.prevent="approveUserWaitingForApproval(data.item)"
            >
              {{ isProcessingApproval(data.item, 'approve') ? 'Approving' : 'Approve' }}
            </b-button>
            <b-button
              variant="danger"
              class="ml-1"
              :disabled="isProcessingAnyApproval()"
              @click.stop.prevent="rejectUserWaitingForApproval(data.item)"
            >
              {{ isProcessingApproval(data.item, 'reject') ? 'Rejecting' : 'Reject' }}
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>

    <b-card>
      <b-card-title class="d-flex justify-content-between align-items-center mb-0">
        <span>Users assigned to {{ company.name }}</span>
        <b-form-group class="mb-0">
          <b-form-radio-group
            v-model="selectedMode"
            :options="modeOptions"
            name="matured-products-mode-selection"
            button-variant="outline-primary"
            buttons
          />
        </b-form-group>
      </b-card-title>
      <br>
      <b-table
        :items="selectedMode === 'active' ? users : usersInactive"
        :fields="usersTableHeader"
        show-empty
        :empty-text="`There are currently no users in this category`"
      >
        <template #cell(actions)="data">
          <div class="d-flex align-items-center">
            <b-button
              v-if="selectedMode === 'active'"
              variant="danger"
              class="d-inline-flex align-items-center text-nowrap ml-1"
              @click.prevent="showUserInactiveModal(data)"
            >
              <feather-icon
                icon="UserXIcon"
                class="mr-50"
              />
              Deactivate
            </b-button>
            <b-button
              v-else
              variant="success"
              class="d-inline-flex align-items-center text-nowrap ml-1"
              @click.prevent="showUserMakeActiveModal(data)"
            >
              <feather-icon
                icon="PlusIcon"
                class="mr-50"
              />
              Activate
            </b-button>
            <b-button
              variant="danger"
              class="d-inline-flex align-items-center text-nowrap ml-1"
              @click.prevent="showRemoveUserFromCompanyModal(data)"
            >
              <feather-icon
                icon="Trash2Icon"
                class="mr-50"
              />
              Delete
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>

    <b-card>
      <b-card-title>Approved email suffixes</b-card-title>
      <b-alert
        v-if="suffixActionError"
        variant="danger"
        show
      >
        {{ suffixActionError }}
      </b-alert>
      <div class="d-flex align-items-center mb-1">
        <b-form-input
          v-model="newSuffix"
          class="mr-1"
          placeholder="email suffix"
          :disabled="processingSuffixAction"
          @keyup.enter="addCompanyEmailSuffix"
        />
        <b-button
          variant="primary"
          :disabled="!newSuffix.trim() || processingSuffixAction"
          @click.prevent="addCompanyEmailSuffix"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          Add
        </b-button>
      </div>
      <b-table
        :items="suffixes"
        :fields="suffixTableHeader"
        show-empty
        empty-text="There are currently no approved email suffixes"
      >
        <template #cell(suffix)="data">
          <b-form-input
            v-if="editingSuffix === data.item.suffix"
            v-model="editSuffixValue"
            :disabled="processingSuffixAction"
            @keyup.enter="updateCompanyEmailSuffix(data.item)"
          />
          <span v-else>{{ data.item.suffix }}</span>
        </template>
        <template #cell(actions)="data">
          <div class="d-flex align-items-center">
            <template v-if="editingSuffix === data.item.suffix">
              <b-button
                variant="success"
                class="btn-icon rounded-circle ml-1"
                :disabled="!editSuffixValue.trim() || processingSuffixAction"
                @click.prevent="updateCompanyEmailSuffix(data.item)"
              >
                <feather-icon icon="CheckIcon" />
              </b-button>
              <b-button
                variant="outline-secondary"
                class="btn-icon rounded-circle ml-1"
                :disabled="processingSuffixAction"
                @click.prevent="cancelEditCompanyEmailSuffix"
              >
                <feather-icon icon="XIcon" />
              </b-button>
            </template>
            <template v-else>
              <b-button
                variant="primary"
                class="btn-icon rounded-circle ml-1"
                :disabled="processingSuffixAction"
                @click.prevent="startEditCompanyEmailSuffix(data.item)"
              >
                <feather-icon icon="Edit2Icon" />
              </b-button>
              <b-button
                variant="danger"
                class="btn-icon rounded-circle ml-1"
                :disabled="suffixes.length <= 1 || processingSuffixAction"
                @click.prevent="confirmDeleteCompanyEmailSuffix(data.item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </template>
          </div>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  BAlert,
  BCard,
  BTable,
  BButton,
  BCardTitle,
  BFormGroup,
  BFormInput,
  BFormRadioGroup,
  BCol,
  BRow,
} from 'bootstrap-vue'

export default {
  name: 'CompanyOverview',
  components: {
    BAlert,
    BCard,
    BTable,
    BButton,
    BCardTitle,
    BFormGroup,
    BFormInput,
    BFormRadioGroup,
    BCol,
    BRow,
  },
  data() {
    return {
      selectedMode: 'active',
      modeOptions: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ],
      company: {
        name: '',
        fcanumber: '',
        subscription: '',
        subscriptioncode: 0,
        calltoaction: false,
      },
      usersHeader: [],
      users: [],
      usersInactive: [],
      pendingSuffixRequests: [],
      pendingSuffixRequestsHeader: [
        { key: 'pendingid', label: 'Pending ID' },
        { key: 'suffix', label: 'Suffix' },
        { key: 'requested', label: 'Requested' },
        { key: 'actions', label: 'Actions' },
      ],
      pendingSuffixError: '',
      loadingPendingSuffixes: false,
      usersWaitingForApprovalHeader: [
        { key: 'fullname', label: 'Name' },
        { key: 'emailname', label: 'Email address' },
        { key: 'actions', label: 'Actions' },
      ],
      usersWaitingForApproval: [],
      approvalActionError: '',
      processingApprovalEmail: '',
      processingApprovalAction: '',
      processingApprovalAllAction: '',
      suffixHeader: [],
      suffixes: [],
      newSuffix: '',
      editingSuffix: '',
      editSuffixValue: '',
      suffixActionError: '',
      processingSuffixAction: false,
      hasAccess: false,
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isCompanyAdmin',
      'companyStatus',
    ]),
    companySubscriptionLabel() {
      if (this.companyStatus === 'trial') {
        return 'Premium (Trial)'
      }

      if (this.companyStatus === 'premium') {
        return 'Premium'
      }

      if (this.companyStatus === 'standard') {
        return 'Standard'
      }

      if (this.company.subscription && this.company.subscription.toLowerCase().indexOf('premium') === 0) {
        return 'Premium'
      }

      return 'Standard'
    },
    usersTableHeader() {
      if (this.companyStatus === 'standard') {
        return this.usersHeader
      }

      return this.usersHeader.filter(field => field.key !== 'premiumstatus')
    },
    suffixTableHeader() {
      return [
        ...this.suffixHeader,
        { key: 'actions', label: 'Actions' },
      ]
    },
  },
  watch: {
    '$route.query.refresh': function refreshCompanyAdminData() {
      this.hasAccess = this.isCompanyAdmin
      if (this.hasAccess) {
        this.loadCompanyAdminData()
      }
    },
  },
  created() {
    this.hasAccess = this.isCompanyAdmin
    if (this.hasAccess) {
      this.loadCompanyAdminData()
    } else {
      this.$router.push({ name: 'home' })
    }
  },
  activated() {
    this.hasAccess = this.isCompanyAdmin
    if (this.hasAccess) {
      this.loadCompanyAdminData()
    } else {
      this.$router.push({ name: 'home' })
    }
  },
  methods: {
    loadCompanyAdminData() {
      this.approvalActionError = ''
      this.processingApprovalEmail = ''
      this.processingApprovalAction = ''
      this.processingApprovalAllAction = ''
      this.suffixActionError = ''
      this.getCompanyOverview()
      this.getPendingSuffixRequests()
      this.getUsersWaitingForApproval()
    },
    async getPendingSuffixRequests() {
      if (this.loadingPendingSuffixes) {
        return
      }

      this.pendingSuffixError = ''
      this.loadingPendingSuffixes = true

      try {
        const response = await this.$http.get('companymanagment.cfc?method=getPendingSuffixRequests')
        if (response.data.status && Array.isArray(response.data.requests)) {
          this.pendingSuffixRequests = response.data.requests
        } else {
          this.pendingSuffixError = 'Pending suffix requests could not be loaded. Please try again.'
        }
      } catch {
        this.pendingSuffixError = 'Pending suffix requests could not be loaded. Please try again.'
      } finally {
        this.loadingPendingSuffixes = false
      }
    },
    getCompanyOverview() {
      console.log('Fetching company overview data...')
      return this.$http.get('companymanagment.cfc?method=companybreakdown')
        .then(response => {
          this.company = response.data.company
          this.users = response.data.users.data.active
          this.usersInactive = response.data.users.data.inactive
          this.usersHeader = response.data.users.header
          this.suffixes = response.data.suffixes.data
          this.suffixHeader = response.data.suffixes.header
          if (this.editingSuffix && !this.suffixes.some(suffix => suffix.suffix === this.editingSuffix)) {
            this.cancelEditCompanyEmailSuffix()
          }
        })
    },
    getUsersWaitingForApproval() {
      return this.$http.get('companymanagment.cfc?method=getUsersWaitingForApproval')
        .then(response => {
          this.usersWaitingForApproval = response.data.status ? response.data.users : []
        })
        .catch(() => {
          this.usersWaitingForApproval = []
        })
    },
    approvalRowClass(item, type) {
      if (!item || type !== 'row') {
        return ''
      }

      return 'approval-row'
    },
    isProcessingApproval(user, action) {
      return !!user
        && this.processingApprovalEmail === user.emailname
        && this.processingApprovalAction === action
    },
    isProcessingAnyApproval() {
      return !!this.processingApprovalEmail || !!this.processingApprovalAllAction
    },
    isProcessingAllApprovals(action) {
      return this.processingApprovalAllAction === action
    },
    goToUserApproval(user) {
      if (!user || !user.emailname) {
        return
      }

      this.$router.push({
        name: 'user-approval',
        params: { userEmail: user.emailname },
      })
    },
    approveUserWaitingForApproval(user) {
      return this.processUserWaitingForApproval(user, 'approve')
    },
    rejectUserWaitingForApproval(user) {
      return this.processUserWaitingForApproval(user, 'reject')
    },
    processUserWaitingForApproval(user, action) {
      if (!user || !user.emailname || this.isProcessingAnyApproval()) {
        return Promise.resolve()
      }

      this.approvalActionError = ''
      this.processingApprovalEmail = user.emailname
      this.processingApprovalAction = action

      return this.submitUserWaitingForApprovalAction(user, action)
        .then(result => {
          if (result.status) {
            this.usersWaitingForApproval = this.usersWaitingForApproval
              .filter(waitingUser => waitingUser.emailname !== user.emailname)
            this.getCompanyOverview()
            this.showApprovalToast(
              `Application ${this.getApprovalActionPastTense(action)}.`,
              'Success',
              'success',
            )
          } else {
            this.approvalActionError = result.errorMessage
            this.showApprovalToast(this.approvalActionError, 'Failed', 'danger')
          }
        })
        .then(() => {
          this.processingApprovalEmail = ''
          this.processingApprovalAction = ''
        })
    },
    submitUserWaitingForApprovalAction(user, action) {
      const method = action === 'approve' ? 'approveUserWaitingForApproval' : 'rejectUserWaitingForApproval'

      return this.$http.post(`companymanagment.cfc?method=${method}`, new URLSearchParams({
        userEmail: user.emailname,
      }))
        .then(response => {
          if (response.data.status) {
            return { status: true }
          }

          return {
            status: false,
            errorMessage: this.getApprovalActionErrorMessage(response.data.reason),
          }
        })
        .catch(() => ({
          status: false,
          errorMessage: 'This application could not be processed. Please try again.',
        }))
    },
    confirmProcessAllUsersWaitingForApproval(action) {
      if (!this.usersWaitingForApproval.length || this.isProcessingAnyApproval()) {
        return Promise.resolve()
      }

      const actionLabel = action === 'approve' ? 'approve' : 'reject'
      const actionTitle = action === 'approve' ? 'Approve all' : 'Reject all'
      const countLabel = this.formatApplicationCount(this.usersWaitingForApproval.length)

      return this.$bvModal
        .msgBoxConfirm(`Are you sure you want to ${actionLabel} all ${countLabel} waiting for approval?`, {
          title: 'Confirm',
          size: 'sm',
          okVariant: action === 'approve' ? 'success' : 'danger',
          okTitle: actionTitle,
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(value => {
          if (value) {
            return this.processAllUsersWaitingForApproval(action)
          }

          return Promise.resolve()
        })
    },
    processAllUsersWaitingForApproval(action) {
      if (!this.usersWaitingForApproval.length || this.isProcessingAnyApproval()) {
        return Promise.resolve()
      }

      const usersToProcess = [...this.usersWaitingForApproval]
      let successfulCount = 0
      let failedCount = 0
      let lastErrorMessage = ''

      this.approvalActionError = ''
      this.processingApprovalAllAction = action

      const processAllRequests = usersToProcess.reduce(
        (requestChain, user) => requestChain
          .then(() => this.submitUserWaitingForApprovalAction(user, action))
          .then(result => {
            if (result.status) {
              successfulCount += 1
              this.usersWaitingForApproval = this.usersWaitingForApproval
                .filter(waitingUser => waitingUser.emailname !== user.emailname)
            } else {
              failedCount += 1
              lastErrorMessage = result.errorMessage
            }
          }),
        Promise.resolve(),
      )

      return processAllRequests
        .then(() => Promise.all([
          this.getCompanyOverview(),
          this.getUsersWaitingForApproval(),
        ]))
        .then(() => {
          if (failedCount) {
            const processedMessage = successfulCount
              ? `${this.formatApplicationCount(successfulCount)} ${this.getApprovalActionPastTense(action)}. `
              : ''

            this.approvalActionError = `${processedMessage}${this.formatApplicationCount(failedCount)} could not be processed. ${lastErrorMessage}`
            this.showApprovalToast(this.approvalActionError, 'Finished with errors', 'danger')
          } else {
            this.showApprovalToast(
              `All ${this.formatApplicationCount(successfulCount)} ${this.getApprovalActionPastTense(action)}.`,
              'Success',
              'success',
            )
          }
        })
        .catch(() => {
          this.approvalActionError = 'These applications could not be processed. Please try again.'
          this.showApprovalToast(this.approvalActionError, 'Error', 'danger')
        })
        .then(() => {
          this.processingApprovalAllAction = ''
        })
    },
    getApprovalActionPastTense(action) {
      return action === 'approve' ? 'approved' : 'rejected'
    },
    formatApplicationCount(count) {
      return `${count} ${count === 1 ? 'application' : 'applications'}`
    },
    getApprovalActionErrorMessage(reason) {
      return reason === 'companycap'
        ? 'This application could not be accepted because your company has reached its user limit.'
        : 'This application could not be processed. It may no longer be waiting for approval, or you may not have access to it.'
    },
    showApprovalToast(message, title, variant) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      })
    },
    async toggleUserActiveStatus(userEmail) {
      return this.$http.post('companymanagment.cfc?method=toggleUserStatus', new URLSearchParams({
        userEmail,
      }))
        .then(response => {
          if (!response.data.status) {
            console.log('error')
          }
        })
    },
    async removeUserFromCompany(userEmail) {
      return this.$http.post('companymanagment.cfc?method=removeUserFromCompany', new URLSearchParams({
        userEmail,
      }))
        .then(response => response.data.status)
        .catch(() => false)
    },
    showUserMakeActiveModal(data) {
      this.$bvModal
        .msgBoxConfirm('Are you sure you make this user active?', {
          title: 'Confirm',
          size: 'sm',
          okVariant: 'primary',
          okTitle: 'Activate',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(async value => {
          if (value) {
            await this.toggleUserActiveStatus(data.item.emailname)
            this.getCompanyOverview()
          }
        })
    },
    showUserInactiveModal(data) {
      const userLabel = `${data.item.fullname} (${data.item.emailname})`

      this.$bvModal
        .msgBoxConfirm(`Are you sure you want to make ${userLabel} inactive?`, {
          title: 'Make user inactive',
          size: 'sm',
          okVariant: 'warning',
          okTitle: 'Deactivate',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(async value => {
          if (value) {
            await this.toggleUserActiveStatus(data.item.emailname)
            this.getCompanyOverview()
          }
        })
    },
    showRemoveUserFromCompanyModal(data) {
      const userLabel = `${data.item.fullname} (${data.item.emailname})`

      this.$bvModal
        .msgBoxConfirm(`Are you sure you want to delete ${userLabel} from your company? They will no longer be able to log in.`, {
          title: 'Delete user from company',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(async value => {
          if (value) {
            const removed = await this.removeUserFromCompany(data.item.emailname)

            if (removed) {
              this.getCompanyOverview()
              this.showApprovalToast('User removed from company.', 'Success', 'success')
            } else {
              this.showApprovalToast('This user could not be removed from your company.', 'Error', 'danger')
            }
          }
        })
    },
    addCompanyEmailSuffix() {
      const suffix = this.newSuffix.trim()

      if (!suffix || this.processingSuffixAction) {
        return Promise.resolve()
      }

      this.suffixActionError = ''
      this.processingSuffixAction = true

      return this.submitCompanyEmailSuffixAction('addCompanyEmailSuffix', { suffix })
        .then(result => {
          if (result.status) {
            this.newSuffix = ''
            this.getCompanyOverview()
            this.showApprovalToast('Email suffix added.', 'Success', 'success')
          } else {
            this.suffixActionError = this.getSuffixActionErrorMessage(result.reason)
            this.showApprovalToast(this.suffixActionError, 'Error', 'danger')
          }
        })
        .then(() => {
          this.processingSuffixAction = false
        })
    },
    startEditCompanyEmailSuffix(suffix) {
      this.suffixActionError = ''
      this.editingSuffix = suffix.suffix
      this.editSuffixValue = suffix.suffix
    },
    cancelEditCompanyEmailSuffix() {
      this.editingSuffix = ''
      this.editSuffixValue = ''
    },
    updateCompanyEmailSuffix(suffix) {
      const newSuffix = this.editSuffixValue.trim()

      if (!newSuffix || this.processingSuffixAction) {
        return Promise.resolve()
      }

      this.suffixActionError = ''
      this.processingSuffixAction = true

      return this.submitCompanyEmailSuffixAction('updateCompanyEmailSuffix', {
        oldSuffix: suffix.suffix,
        newSuffix,
      })
        .then(result => {
          if (result.status) {
            this.cancelEditCompanyEmailSuffix()
            this.getCompanyOverview()
            this.showApprovalToast('Email suffix updated.', 'Success', 'success')
          } else {
            this.suffixActionError = this.getSuffixActionErrorMessage(result.reason)
            this.showApprovalToast(this.suffixActionError, 'Error', 'danger')
          }
        })
        .then(() => {
          this.processingSuffixAction = false
        })
    },
    confirmDeleteCompanyEmailSuffix(suffix) {
      if (this.suffixes.length <= 1 || this.processingSuffixAction) {
        this.suffixActionError = this.getSuffixActionErrorMessage('lastsuffix')
        this.showApprovalToast(this.suffixActionError, 'Error', 'danger')
        return Promise.resolve()
      }

      return this.$bvModal
        .msgBoxConfirm(`Are you sure you want to delete ${suffix.suffix}?`, {
          title: 'Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(value => {
          if (value) {
            return this.deleteCompanyEmailSuffix(suffix)
          }

          return Promise.resolve()
        })
    },
    deleteCompanyEmailSuffix(suffix) {
      this.suffixActionError = ''
      this.processingSuffixAction = true

      return this.submitCompanyEmailSuffixAction('deleteCompanyEmailSuffix', {
        suffix: suffix.suffix,
      })
        .then(result => {
          if (result.status) {
            this.cancelEditCompanyEmailSuffix()
            this.getCompanyOverview()
            this.showApprovalToast('Email suffix deleted.', 'Success', 'success')
          } else {
            this.suffixActionError = this.getSuffixActionErrorMessage(result.reason)
            this.showApprovalToast(this.suffixActionError, 'Error', 'danger')
          }
        })
        .then(() => {
          this.processingSuffixAction = false
        })
    },
    submitCompanyEmailSuffixAction(method, params) {
      return this.$http.post(`companymanagment.cfc?method=${method}`, new URLSearchParams(params))
        .then(response => ({
          status: response.data.status,
          reason: response.data.reason,
        }))
        .catch(() => ({
          status: false,
          reason: 'error',
        }))
    },
    getSuffixActionErrorMessage(reason) {
      if (reason === 'lastsuffix') {
        return 'At least one approved email suffix is required.'
      }

      if (reason === 'duplicate') {
        return 'This email suffix already exists.'
      }

      if (reason === 'missing') {
        return 'Enter an email suffix.'
      }

      if (reason === 'notfound') {
        return 'This email suffix could not be found.'
      }

      return 'This email suffix could not be updated.'
    },
  },
}
</script>

<style scoped>
::v-deep .approval-row {
  cursor: pointer;
}
</style>
